
import { useState, useEffect } from 'react';
import { useTelegramWebApp } from './useTelegramWebApp';
import { supabase } from '@/integrations/supabase/client';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface BroUser {
  id: string;
  telegram_id: string;
  brocode: string;
  rank: string;
  bro_score: number;
  is_brometheus: boolean;
  twitter_handle?: string;
}

export const useTelegramAuth = () => {
  const [user, setUser] = useState<BroUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const { webApp, isTelegram } = useTelegramWebApp();

  useEffect(() => {
    const authenticateUser = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('🔄 Starting authentication...', { isTelegram, webAppReady: !!webApp });

        if (!isTelegram) {
          console.log('🌐 Running in web mode (not Telegram)');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        if (!webApp) {
          console.log('⏳ Telegram WebApp not ready yet');
          setIsLoading(false);
          return;
        }

        if (!webApp.initDataUnsafe?.user) {
          console.warn('⚠️ No Telegram user data available');
          setError('Unable to access Telegram user data. Please restart the mini-app.');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const telegramUser: TelegramUser = webApp.initDataUnsafe.user;
        console.log('📱 Authenticating Telegram user:', telegramUser.id, telegramUser.username);

        // Store the raw initData for API calls
        setInitData(webApp.initData || null);

        // Query the database directly using telegram_id
        console.log('🔍 Querying database for telegram_id:', telegramUser.id.toString());
        const { data: broUsers, error: dbError } = await supabase
          .from('initiated_bros')
          .select('*')
          .eq('telegram_id', telegramUser.id.toString())
          .order('created_at', { ascending: false });

        console.log('🗄️ Database query result:', { broUsers, dbError });

        let broUser = null;
        if (broUsers && broUsers.length > 0) {
          // If multiple records exist, use the most recent one
          broUser = broUsers[0];
          if (broUsers.length > 1) {
            console.warn('⚠️ Multiple records found for telegram_id:', telegramUser.id.toString(), 'Using most recent');
          }
        }

        if (dbError) {
          console.error('❌ Database error:', dbError);
          setError(`Database error: ${dbError.message}`);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        if (!broUser) {
          console.log('👤 User not found in database, needs initiation');
          // For new users, don't set this as an error - they need to complete initiation
          setError(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        console.log('✅ Successfully authenticated user:', broUser.brocode);
        
        // CRITICAL FIX: Store BroCode in localStorage for API authentication
        try {
          localStorage.setItem('brocode', broUser.brocode);
          localStorage.setItem('user_brocode', broUser.brocode); // Backup key
          console.log('💾 Stored BroCode in localStorage for API calls:', broUser.brocode);
        } catch (error) {
          console.error('❌ Failed to store BroCode in localStorage:', error);
        }
        
        setUser({
          id: broUser.id,
          telegram_id: broUser.telegram_id ?? '',
          brocode: broUser.brocode,
          rank: broUser.rank,
          bro_score: broUser.bro_score || 0,
          is_brometheus: broUser.is_brometheus || false,
          twitter_handle: broUser.twitter_handle ?? undefined,
        });
        setIsAuthenticated(true);

      } catch (error) {
        console.error('❌ Authentication error:', error);
        setError('Authentication failed. Please try again or contact support.');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    authenticateUser();
  }, [webApp, isTelegram]);

  const retryAuth = () => {
    setError(null);
    setIsLoading(true);
    // Re-trigger the authentication
    window.location.reload();
  };

  const clearAuthData = () => {
    try {
      localStorage.removeItem('brocode');
      localStorage.removeItem('user_brocode');
      console.log('🧹 Cleared BroCode from localStorage');
    } catch (error) {
      console.error('❌ Failed to clear BroCode from localStorage:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    retryAuth,
    clearAuthData,
    initData, // Expose initData for API calls
  };
};
